import { type BaseRendererComponentProps, BaseModel, BaseModelProps } from '@mail/3promo';
import { ReactRenderer } from '@mail/3promo/render/react';
import { LocalStorageStore } from '@mail/3promo/stores/localStorage';
import type { ModelCreator } from '@mail/3promo/types';
import React from 'react';
import {
    AI_VIEWER_TOOLBAR_ID,
    closeLimitTooltip,
    isAiRemoveBackgroundEnabled,
    PROMO_TOOLTIP_POPUP_ID,
    showIntervalTooltip,
    showLimitTooltip,
    storageKeyForPopup,
    storageKeyForTootip,
} from 'reactApp/appHelpers/featuresHelpers/features/imageTools';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { ViewerSelectors } from 'reactApp/modules/viewer/viewer.selectors';
import { getDataFromStorage } from 'reactApp/storage';
import { store as reduxStore } from 'reactApp/store';
import { sendXray } from 'reactApp/utils/ga';

class TooltipModel extends BaseModel<BaseModelProps> {
    getParent(): HTMLElement | null {
        return document.getElementById(AI_VIEWER_TOOLBAR_ID);
    }
}

const oneDayInSeconds = 60 * 60 * 24;

const sendRadar = (name) => sendXray(['promo-test', PROMO_TOOLTIP_POPUP_ID, name]);

const LazyComponent = React.lazy(() => import('reactApp/ui/ReactViewer/AiImprove/PromoTooltip/AiRmvViewerPromoTooltip'));

const SuspendedComponent = (props: BaseRendererComponentProps) => {
    return (
        <React.Suspense fallback={null}>
            <LazyComponent {...props} />
        </React.Suspense>
    );
};

export const aiRmvViewerPromoTooltipCreator: ModelCreator = ({ models, render, stores }) =>
    new TooltipModel(
        PROMO_TOOLTIP_POPUP_ID,
        models.base,
        new BaseModelProps(PROMO_TOOLTIP_POPUP_ID, {
            store: new LocalStorageStore(storageKeyForTootip, stores.localStorage),
            renderer: new ReactRenderer(PROMO_TOOLTIP_POPUP_ID, SuspendedComponent, render.react),
            closable: true,
            showLimit: showLimitTooltip,
            closeLimit: closeLimitTooltip,
            showInterval: showIntervalTooltip,
            templateData: {},
            ignoreTestOnRedraw: true,
            test: (resolve, reject) => {
                const state = reduxStore.getState();
                const storage = getCurrentStorage(state);
                const isViewerActive = ViewerSelectors.isViewerActive(state);

                if (!isAiRemoveBackgroundEnabled || !isViewerActive) {
                    reject();
                    return;
                }

                if (
                    ![
                        EStorageType.home,
                        EStorageType.favorites,
                        EStorageType.gallery,
                        EStorageType.feed,
                        EStorageType.viewerAttaches,
                        EStorageType.public,
                    ].includes(storage)
                ) {
                    reject();
                    return;
                }

                const { action = '' } = SettingsSelectors.getQueryParams(state);
                if (action) {
                    reject();
                    sendRadar('rejectbyaction');
                    return;
                }

                const data = getDataFromStorage(`3promo:${storageKeyForPopup}`);

                if (!data?.time) {
                    sendXray(['promo-test', PROMO_TOOLTIP_POPUP_ID, 'rjct_noppp']);
                    reject();
                    return;
                }

                // Показываем тока на след день (и потом через день от этого) после показа попапа
                const res = (Math.trunc((Date.now().valueOf() / 1000 - data?.time) / oneDayInSeconds) - 1) % 2;

                if (res !== 0) {
                    sendRadar('rjct_pppdays');
                    reject();
                    return;
                }

                const isBizUser = UserSelectors.isBizUser(state);
                const isCorpUser = UserSelectors.isCorpUser(state);
                const isPaidUser = UserSelectors.isPaidUser(state);
                const isOverquota = UserQuotaSelectors.isOverquota(state);

                const isReject = isBizUser || isPaidUser || isOverquota || isCorpUser;

                sendRadar(isReject ? 'reject' : 'resolve');

                if (isReject) {
                    reject();
                    return;
                }

                resolve();
            },
        })
    );
